<template>
  <div class="projects-page">
    <TheNav />
    <TheActions />

    <div class="container">
      <TheBackLink />

      <div class="section-heading">
        <div class="heading-line"/>
        <h1 class="section-title">All Projects</h1>
      </div>
      <p class="section-subtitle">
        Everything I've shipped: client work, experiments, and packages.
      </p>

      <div class="projects-grid">
        <component
          :is="getProjectLink(project) ? 'a' : 'button'"
          v-for="project in allProjects"
          :key="project.id"
          :href="getProjectLink(project) || undefined"
          :target="getProjectLink(project) ? '_blank' : undefined"
          :rel="getProjectLink(project) ? 'noopener noreferrer' : undefined"
          class="project-card"
          :class="{ 'is-static': !getProjectLink(project) }"
          :type="getProjectLink(project) ? undefined : 'button'"
          @click="!getProjectLink(project) && (unavailableProject = project)"
        >
          <div class="project-card__image-wrapper">
            <!-- No screenshot yet: show what the project IS rather than a
                 stock photo that implies it's the real interface. The corner
                 marker is skipped here since the glyph already says it. -->
            <template v-if="project.thumbnail">
              <!-- Eighteen cards stack down this page, so all but the first
                   few are below the fold on arrival. -->
              <img
                :src="project.thumbnail"
                :alt="project.title"
                class="project-card__image"
                loading="lazy"
                decoding="async"
              >
              <span
                v-if="glyphName(project)"
                class="project-card__type"
                :title="typeLabel(project.type)"
                :aria-label="typeLabel(project.type)"
              >
                <ProjectGlyph :name="glyphName(project)" :size="16" />
              </span>
            </template>
            <div
              v-else
              class="project-card__placeholder"
              :title="typeLabel(project.type)"
              :aria-label="typeLabel(project.type)"
            >
              <ProjectGlyph :name="glyphName(project)" :size="46" :stroke-width="1.4" />
            </div>
          </div>

          <div class="project-card__content">
            <h2 class="project-card__title">
              <span>{{ project.title }}</span>
              <span v-if="getLinkIconType(project)" class="project-card__icon">
                <svg v-if="getLinkIconType(project) === 'external'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7"/>
                  <polyline points="8 7 17 7 17 16"/>
                </svg>
                <svg v-else-if="getLinkIconType(project) === 'github'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                </svg>
                <img v-else-if="getLinkIconType(project) === 'npm'" src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/npm/npm-original-wordmark.svg" alt="npm" class="project-card__npm-icon" decoding="async">
              </span>
            </h2>
            <p class="project-card__description">{{ project.description }}</p>
          </div>
        </component>
      </div>
    </div>

    <ProjectUnavailableModal :project="unavailableProject" @close="unavailableProject = null" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { getAllProjects, personalInfo } from '~/data/content';

usePageScroll();

const allProjects = computed(() => getAllProjects());

const TYPE_LABELS = {
  web: 'Web',
  app: 'App',
  hybrid: 'Hybrid — mobile and software',
};

function typeLabel(type) {
  return TYPE_LABELS[type] || '';
}

function getProjectLink(project) {
  return project.demoUrl || project.repoUrl || null;
}

// Same mapping the home grid uses: npm packages get the npm mark, a live demo
// gets the external-link arrow, repo-only projects get GitHub's.
// The card artwork/marker says what KIND of project this is: an npm package
// shows npm's mark, a repo-only project shows GitHub's, and anything else
// falls back to its build type (web / app / hybrid).
function glyphName(project) {
  if (project.category === 'package') return 'npm';
  // An explicit build type wins; otherwise anything with source available
  // shows GitHub's mark, and the last resort is the generic web glyph — so
  // no card can ever render blank artwork.
  if (project.type) return project.type;
  if (project.repoUrl) return 'github';
  return 'web';
}

function getLinkIconType(project) {
  if (project.category === 'package') return 'npm';
  if (project.demoUrl) return 'external';
  if (project.repoUrl) return 'github';
  return null;
}

useHead({
  title: `Projects - ${personalInfo.fullName}`,
  meta: [
    { name: 'description', content: `Explore all projects by ${personalInfo.fullName}` },
    { property: 'og:title', content: `Projects - ${personalInfo.fullName}` },
    { property: 'og:description', content: `Explore all projects by ${personalInfo.fullName}` },
    { name: 'twitter:title', content: `Projects - ${personalInfo.fullName}` },
    { name: 'twitter:description', content: `Explore all projects by ${personalInfo.fullName}` },
  ]
});

// Cards with no public link are still clickable — they just say so. Silently
// doing nothing reads as a broken card, and these are real projects that simply
// have nothing to link to yet.
const unavailableProject = ref(null);
</script>

<style lang="scss" scoped>
// A normal vertical page: it sits outside the horizontal track, so it scrolls
// conventionally rather than being pinned.
.projects-page {
  min-height: 100vh;
  min-height: 100svh;
  padding: clamp(3rem, 8vh, 5rem) 0 clamp(3rem, 8vh, 5rem);
  background: transparent;
}


.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0 0 clamp(1.5rem, 4vh, 2.5rem);
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(0.75rem, 1vw + 1vh, 1.5rem);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.project-card {
  // Linkless cards render as <button>, which arrives with its own font, text
  // alignment and border. Reset here so both kinds of card look identical.
  font: inherit;
  text-align: left;
  width: 100%;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  // Same tint as before, but over an opaque base so nothing shows through.
  background:
    linear-gradient(rgba(var(--muted-rgb), 0.04), rgba(var(--muted-rgb), 0.04)),
    var(--background);
  border: 1px solid rgba(var(--border-rgb), 0.25);
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(var(--accent-rgb), 0.4);
    text-decoration: none;

    .project-card__icon {
      transform: translate(2px, -2px);
    }
  }

  // Projects with no link aren't interactive, so they shouldn't offer a
  // pointer or a hover lift that implies they are.
  &.is-static {
    cursor: default;
  }
}

.project-card__type {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(10, 14, 20, 0.55);
  color: #fff;
}

.project-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--accent-rgb), 0.07);
  color: rgba(var(--accent-rgb), 0.75);
}

.project-card__image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  flex-shrink: 0;
}

.project-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-card__content {
  padding: clamp(0.7rem, 0.5vw + 0.8vh, 1rem);
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, 0.6vh, 0.4rem);
  flex-grow: 1;
}

.project-card__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: clamp(0.9rem, 0.5vw + 0.8vh, 1.05rem);
  font-weight: 700;
  color: var(--text);
  margin: 0;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.project-card__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: var(--text);
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;

  .project-card__npm-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}

.project-card__description {
  color: var(--muted);
  font-size: clamp(0.75rem, 0.3vw + 0.7vh, 0.85rem);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
